import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../auth/Auth';
import { getReferralStats } from '../../../../auth/_requests';
import { usePointTasksContext } from '../../../../helpers/hooks/pointTasksContext';
import {
  getTaskMaxPoints,
  getTaskPointsText,
  getTaskThresholdText,
  getTaskTicketsText,
} from '../../../../helpers/utils/rewardLabels';
import type { Offer, ReferralStats } from '../../../../helpers/utils/types';

const REFERRAL_TIER_CODES = [
  'referralBonusTier1',
  'referralBonusTier2',
  'referralBonusTier3',
] as const;

const TIER_LAYOUT = [
  { tag: 'Tier 1', variant: '' as const },
  { tag: 'Tier 2', variant: 'gold' as const },
  { tag: 'Tier 3', variant: 'green' as const },
];

const parseTierGoal = (task: Offer) => {
  const fromProgress = Number(task.progress?.totalCount ?? 0);
  if (Number.isFinite(fromProgress) && fromProgress > 0) return `${fromProgress} refs`;
  const fromDescription = task.description?.match(/(\d+)\s+friends?/i)?.[1];
  return fromDescription ? `${fromDescription} refs` : '';
};

const formatTierReward = (task: Offer) => {
  const value = getTaskMaxPoints(task);
  return value > 0 ? `+${value.toLocaleString('en-US')} pts` : '';
};

export const EarnReferralSummary = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { getTasksByCode } = usePointTasksContext();
  const [stats, setStats] = useState<ReferralStats | undefined>(undefined);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const token = auth?.data?.token;
    if (!token) {
      setStats(undefined);
      setStatsLoading(false);
      return;
    }

    let cancelled = false;
    setStatsLoading(true);

    getReferralStats(token)
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(undefined);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [auth?.data?.token]);

  const referralTasks = useMemo(
    () =>
      getTasksByCode([
        'referralReward',
        'referralBonusTier1',
        'referralBonusTier2',
        'referralBonusTier3',
      ]),
    [getTasksByCode]
  );

  const referralRewardTask = referralTasks.find((t) => t.code === 'referralReward');
  const ticketsText = getTaskTicketsText(referralRewardTask);
  const pointsText = getTaskPointsText(referralRewardTask);
  const thresholdText = getTaskThresholdText(referralRewardTask);

  const invited = Number(stats?.totalInvited ?? 0) + Number(stats?.totalUninvited ?? 0);
  const converted = Number(stats?.qualifiedReferrals ?? 0);
  const earnedPts = converted * getTaskMaxPoints(referralRewardTask);

  const tiers = REFERRAL_TIER_CODES.flatMap((code, index) => {
    const task = referralTasks.find((item) => item.code === code);
    if (!task) return [];
    return [
      {
        ...TIER_LAYOUT[index],
        reward: formatTierReward(task),
        goal: parseTierGoal(task),
      },
    ];
  });

  return (
    <div className='en-ref-summary'>
      <div className='en-ref-summary-eyebrow'>Refer & earn</div>
      <h4 className='en-ref-summary-title'>Invite friends, stack rewards</h4>
      <p className='en-ref-summary-sub'>
        When a friend signs up and makes their first{' '}
        {thresholdText ? `${thresholdText}+ ` : ''}payment, you both get rewarded, every time.
      </p>
      <div className='en-ref-summary-rewards'>
        {ticketsText ? <span className='en-ref-summary-pill'>{ticketsText}</span> : null}
        {pointsText ? (
          <span className='en-ref-summary-pill blue'>{pointsText}</span>
        ) : null}
      </div>
      <div
        className='en-ref-summary-stats'
        aria-busy={statsLoading}
        aria-label={statsLoading ? 'Loading referral stats' : undefined}
      >
        <div>
          <div
            className={`en-ref-summary-stat-val${statsLoading ? ' loading' : ''}`}
            aria-hidden={statsLoading}
          >
            {!statsLoading ? invited : null}
          </div>
          <div className='en-ref-summary-stat-lbl'>Invited</div>
        </div>
        <div>
          <div
            className={`en-ref-summary-stat-val${statsLoading ? ' loading' : ''}`}
            aria-hidden={statsLoading}
          >
            {!statsLoading ? converted : null}
          </div>
          <div className='en-ref-summary-stat-lbl'>Converted</div>
        </div>
        <div>
          <div
            className={`en-ref-summary-stat-val${statsLoading ? ' loading' : ''}`}
            aria-hidden={statsLoading}
          >
            {!statsLoading
              ? earnedPts >= 1000
                ? `${Math.round(earnedPts / 1000)}k`
                : earnedPts
              : null}
          </div>
          <div className='en-ref-summary-stat-lbl'>Earned</div>
        </div>
      </div>
      <div className='en-ref-summary-tiers'>
        {tiers.map((tier) => (
          <div
            key={tier.tag}
            className={`en-ref-summary-tier${tier.variant ? ` ${tier.variant}` : ''}`}
          >
            <span className='en-ref-summary-tier-tag'>{tier.tag}</span>
            <span className='en-ref-summary-tier-reward'>{tier.reward}</span>
            <span className='en-ref-summary-tier-goal'>{tier.goal}</span>
          </div>
        ))}
      </div>
      <button type='button' className='en-ref-summary-cta' onClick={() => navigate('/rewards/referrals')}>
        Go to referrals
        <svg
          width='14'
          height='14'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.4'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
        >
          <line x1='5' y1='12' x2='19' y2='12' />
          <polyline points='12 5 19 12 12 19' />
        </svg>
      </button>
    </div>
  );
};
